import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sun, Wind, Heart, CheckCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function MorningCalmGuide() {
  const navigate = useNavigate();

  const routine = [
    { step: 1, action: "Don't Check Your Phone", time: "First 10 min", why: "Starting with emails/news triggers stress hormones immediately" },
    { step: 2, action: "Sit Up and Breathe", time: "1 min", why: "5 deep breaths before standing signals calm to your nervous system" },
    { step: 3, action: "Set an Intention", time: "1 min", why: "Ask: 'How do I want to feel today?' State it as if already true" },
    { step: 4, action: "Meditate or Sit Quietly", time: "5-10 min", why: "Even 5 minutes reduces cortisol and improves focus all day" },
    { step: 5, action: "Move Gently", time: "5 min", why: "Stretching or walking releases overnight stiffness and boosts circulation" }
  ];

  const meditationOptions = [
    { type: "Guided App", examples: "Calm, Headspace, Insight Timer", good: "Beginners, wandering minds" },
    { type: "Breathing Focus", examples: "Count breaths 1-10, repeat", good: "Simple, no equipment" },
    { type: "Body Scan", examples: "Notice each body part from toes up", good: "Physical tension relief" },
    { type: "Mantra Repetition", examples: "'I am calm,' 'Peace,' 'Let go'", good: "Busy minds" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-4">
            Week 6 • Day 2 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Morning Calm Practice
          </h1>
          <p className="text-xl text-secondary-600">Start Your Day with Peace, Not Panic</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-amber-400 to-orange-400 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Sun className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today's Practice</h3>
              <p className="text-amber-100 text-lg">
                Before checking your phone, sit for 10 minutes. Close your eyes, breathe slowly, and set an intention 
                for a calm day. This simple practice reduces stress reactivity by 40%.
              </p>
            </div>
          </div>
        </section>

        {/* Morning Routine */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <Clock className="w-6 h-6 text-amber-500" />
            The Calm Morning Routine
          </h2>
          <div className="space-y-4">
            {routine.map((item) => (
              <div key={item.step} className="flex items-start gap-4 p-4 bg-amber-50 rounded-xl">
                <div className="w-10 h-10 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  {item.step}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <h3 className="font-bold text-secondary-900">{item.action}</h3>
                    <span className="text-xs bg-amber-200 text-amber-700 px-2 py-1 rounded-full">{item.time}</span>
                  </div>
                  <p className="text-sm text-secondary-600">{item.why}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Meditation Options */}
        <section className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border-2 border-amber-200">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Meditation Styles</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {meditationOptions.map((option, index) => (
              <div key={index} className="bg-white rounded-xl p-4">
                <h3 className="font-bold text-amber-700 mb-1">{option.type}</h3>
                <p className="text-sm text-secondary-600 mb-2">{option.examples}</p>
                <p className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full inline-block">Good for: {option.good}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default MorningCalmGuide;
